import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../ui/theme';

// Tag category definitions (shared) - values are kept in Chinese for database consistency
export const tagCategories = {
  emotion: {
    labelKey: 'tags.category.emotion',
    tags: [
      { value: '轻松', translationKey: 'tags.emotion.relaxed' },
      { value: '治愈', translationKey: 'tags.emotion.healing' },
      { value: '悲伤', translationKey: 'tags.emotion.sad' },
      { value: '欢快', translationKey: 'tags.emotion.cheerful' },
      { value: '安静', translationKey: 'tags.emotion.quiet' },
      { value: '激昂', translationKey: 'tags.emotion.passionate' },
      { value: '温柔', translationKey: 'tags.emotion.gentle' },
      { value: '忧郁', translationKey: 'tags.emotion.melancholic' },
      { value: '温暖', translationKey: 'tags.emotion.warm' },
      { value: '清新', translationKey: 'tags.emotion.fresh' },
      { value: '浪漫', translationKey: 'tags.emotion.romantic' },
      { value: '励志', translationKey: 'tags.emotion.inspirational' },
      { value: '舒缓', translationKey: 'tags.emotion.soothing' },
      { value: '深沉', translationKey: 'tags.emotion.deep' },
      { value: '活泼', translationKey: 'tags.emotion.lively' }
    ]
  },
  scene: {
    labelKey: 'tags.category.scene',
    tags: [
      { value: '工作', translationKey: 'tags.scene.work' },
      { value: '学习', translationKey: 'tags.scene.study' },
      { value: '运动', translationKey: 'tags.scene.exercise' },
      { value: '睡前', translationKey: 'tags.scene.beforeSleep' },
      { value: '明想', translationKey: 'tags.scene.meditation' },
      { value: '阅读', translationKey: 'tags.scene.reading' },
      { value: '旅行', translationKey: 'tags.scene.travel' },
      { value: '开车', translationKey: 'tags.scene.driving' },
      { value: '咖啡厅', translationKey: 'tags.scene.cafe' },
      { value: '背景音乐', translationKey: 'tags.scene.background' },
      { value: '放松', translationKey: 'tags.scene.relax' },
      { value: '专注', translationKey: 'tags.scene.focus' }
    ]
  },
  style: {
    labelKey: 'tags.category.style',
    tags: [
      { value: '纯音乐', translationKey: 'tags.style.instrumental' },
      { value: '器乐', translationKey: 'tags.style.instrument' },
      { value: '人声', translationKey: 'tags.style.vocal' },
      { value: '钢琴', translationKey: 'tags.style.piano' },
      { value: '吉他', translationKey: 'tags.style.guitar' },
      { value: '小提琴', translationKey: 'tags.style.violin' },
      { value: '古筝', translationKey: 'tags.style.guzheng' },
      { value: '二胡', translationKey: 'tags.style.erhu' },
      { value: '电子', translationKey: 'tags.style.electronic' },
      { value: '民谣', translationKey: 'tags.style.folk' },
      { value: '爵士', translationKey: 'tags.style.jazz' },
      { value: '古典', translationKey: 'tags.style.classical' },
      { value: '新世纪', translationKey: 'tags.style.newAge' },
      { value: '轻音乐', translationKey: 'tags.style.light' },
      { value: '原声', translationKey: 'tags.style.acoustic' }
    ]
  },
  theme: {
    labelKey: 'tags.category.theme',
    tags: [
      { value: '爱情', translationKey: 'tags.theme.love' },
      { value: '友情', translationKey: 'tags.theme.friendship' },
      { value: '亲情', translationKey: 'tags.theme.family' },
      { value: '自然', translationKey: 'tags.theme.nature' },
      { value: '四季', translationKey: 'tags.theme.seasons' },
      { value: '春天', translationKey: 'tags.theme.spring' },
      { value: '夏天', translationKey: 'tags.theme.summer' },
      { value: '秋天', translationKey: 'tags.theme.autumn' },
      { value: '冬天', translationKey: 'tags.theme.winter' },
      { value: '海洋', translationKey: 'tags.theme.ocean' },
      { value: '星空', translationKey: 'tags.theme.starrySky' },
      { value: '回忆', translationKey: 'tags.theme.memory' },
      { value: '思念', translationKey: 'tags.theme.longing' },
      { value: '成长', translationKey: 'tags.theme.growth' },
      { value: '梦想', translationKey: 'tags.theme.dream' }
    ]
  },
  language: {
    labelKey: 'tags.category.language',
    tags: [
      { value: '中文', translationKey: 'tags.language.chinese' },
      { value: '日文', translationKey: 'tags.language.japanese' },
      { value: '英文', translationKey: 'tags.language.english' },
      { value: '韩文', translationKey: 'tags.language.korean' },
      { value: '中国风', translationKey: 'tags.language.chineseStyle' },
      { value: '和风', translationKey: 'tags.language.japaneseStyle' },
      { value: '欧美', translationKey: 'tags.language.western' },
      { value: '亚洲', translationKey: 'tags.language.asian' }
    ]
  },
  other: {
    labelKey: 'tags.category.other',
    tags: [
      { value: '经典', translationKey: 'tags.other.classic' },
      { value: '流行', translationKey: 'tags.other.popular' },
      { value: '独立', translationKey: 'tags.other.indie' },
      { value: '小众', translationKey: 'tags.other.niche' },
      { value: '推荐', translationKey: 'tags.other.recommended' },
      { value: '精选', translationKey: 'tags.other.featured' },
      { value: '新歌', translationKey: 'tags.other.new' },
      { value: '老歌', translationKey: 'tags.other.old' }
    ]
  }
};

// Music type options (shared) - values are kept in Chinese for database consistency
export const musicTypes = [
  { value: '流行', translationKey: 'musicTypes.pop' },
  { value: '古典', translationKey: 'musicTypes.classical' },
  { value: '民谣', translationKey: 'musicTypes.folk' },
  { value: '乡村', translationKey: 'musicTypes.country' },
  { value: '灵魂乐', translationKey: 'musicTypes.soul' },
  { value: '电子曲', translationKey: 'musicTypes.electronic' },
  { value: '蓝调', translationKey: 'musicTypes.blues' },
  { value: '摇滚', translationKey: 'musicTypes.rock' },
  { value: '说唱', translationKey: 'musicTypes.rap' },
  { value: '爵士', translationKey: 'musicTypes.jazz' }
];

// Unified small form control styles (shared)
export const smallFormStyles = {
  input: [theme.inputs.base, 'px-1.5 py-0.5 text-xs'].join(' '),
  select: [theme.inputs.base, 'px-1.5 py-0.5 text-xs'].join(' '),
  textarea: [theme.inputs.base, 'px-1.5 py-0.5 text-xs'].join(' '),
  label: 'block text-xs font-medium text-white/80 mb-1',
  button: 'px-2 py-0.5 text-xs rounded',
  buttonPrimary: [theme.buttons.base, theme.buttons.size.xs, theme.buttons.variant.outline].join(' '),
  buttonNeutral: [theme.buttons.base, theme.buttons.size.xs, theme.buttons.variant.ghost].join(' ')
};

// Tag handler Hook
export const useTagHandler = (formData, setFormData) => {
  const handleTagToggle = React.useCallback((tag) => {
    const currentTags = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const isSelected = currentTags.includes(tag);
    
    let newTags;
    if (isSelected) {
      // Deselect
      newTags = currentTags.filter(t => t !== tag);
    } else {
      // Add selection
      newTags = [...currentTags, tag];
    }
    
    setFormData(prev => ({
      ...prev,
      tags: newTags.join(',')
    }));
  }, [formData.tags, setFormData]);

  const clearAllTags = React.useCallback(() => {
    setFormData(prev => ({ ...prev, tags: '' }));
  }, [setFormData]);

  return { handleTagToggle, clearAllTags };
};

// Music type radio button group component
export const MusicTypeSelector = ({ formData, onChange, disabled = false }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-3 gap-1">
        {musicTypes.map((type) => (
          <label key={type.value} className="flex items-center cursor-pointer hover:bg-white/[0.04] px-1.5 py-0.5 rounded">
            <input
              type="radio"
              name="music_type"
              value={type.value}
              checked={formData.music_type === type.value}
              onChange={onChange}
              className="mr-1.5 text-blue-600 focus:ring-blue-500"
              disabled={disabled}
            />
            <span className="text-xs text-white/80 leading-tight">{t(type.translationKey)}</span>
          </label>
        ))}
      </div>
      {/* Add "None" option to allow deselection */}
      <label className="flex items-center cursor-pointer hover:bg-white/[0.04] px-1.5 py-0.5 rounded">
        <input
          type="radio"
          name="music_type"
          value=""
          checked={formData.music_type === ''}
          onChange={onChange}
          className="mr-1.5 text-blue-600 focus:ring-blue-500"
          disabled={disabled}
        />
        <span className="text-xs text-white/55 leading-tight">{t('musicForm.none')}</span>
      </label>
    </div>
  );
};

// Tag multi-select interface component
export const TagSelector = ({ formData, onTagToggle, onClearAll, disabled = false }) => {
  const { t } = useTranslation();
  const currentTags = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-white/80">
          {t('musicForm.tagsLabel')}
        </label>
        {formData.tags && (
          <button
            type="button"
            onClick={onClearAll}
            className={theme.links.subtleUnderline}
            disabled={disabled}
          >
            {t('musicForm.clearAll')}
          </button>
        )}
      </div>
      <div className="space-y-2.5 border border-white/10 rounded-lg p-3 bg-white/[0.03]">
        {Object.entries(tagCategories).map(([categoryKey, categoryData]) => (
          <div key={categoryKey} className="border-b border-white/10 pb-2 last:border-b-0 last:pb-0">
            <h4 className="text-xs font-semibold text-white mb-1.5">{t(categoryData.labelKey)}</h4>
            <div className="flex flex-wrap gap-1">
              {categoryData.tags.map((tag) => {
                const isSelected = currentTags.includes(tag.value);
                return (
                  <label
                    key={tag.value}
                    className={`flex items-center px-1.5 py-0.5 rounded cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-500/15 text-white border border-blue-400/30'
                        : 'bg-white/[0.03] text-white/80 border border-white/10 hover:bg-white/[0.05]'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onTagToggle(tag.value)}
                      className="sr-only"
                      disabled={disabled}
                    />
                    <span className="text-xs leading-tight">{t(tag.translationKey)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {formData.tags && (
        <p className="mt-2 text-xs text-white/55">
          {t('musicForm.selectedTags')}: {formData.tags.split(',').map(t => t.trim()).filter(Boolean).join(', ')}
        </p>
      )}
    </div>
  );
};
